import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  deviceApiServerQuery,
  hostOf,
  moveDevice,
  MOVE_STATE_CLASS,
  MOVE_STATE_LABEL,
} from '../api/agentApiMove'
import { ApiError } from '../api/client'
import { toast } from '../ui/notify'

/**
 * Fila Health a aparatului: "Move to api.optimumdesk.ai" - comanda `MVA`
 * pentru masina asta. Numai pentru administratorii de platforma: pentru
 * ceilalti capatul raspunde 403 si cardul nu se deseneaza deloc.
 *
 * Tinta vine de la server (adresa din comenzile de instalare). Butonul e
 * inchis cat o mutare e inca pe drum, si pe un agent clasic 7.x, care nu stie
 * comanda.
 *
 * Un aparat deja pe gazda tintei nu primeste buton deloc: cardul spune
 * "Already on ..." (serverul refuza oricum mutarea, cu 409). Altfel se arata
 * unde vorbeste acum, sau ca nu se stie.
 */
export function DeviceApiServer({ id }: { id: number }) {
  const query = useQueryClient()
  const info = useQuery(deviceApiServerQuery(id))
  const [busy, setBusy] = useState(false)

  const data = info.data
  if (!data || typeof data.target !== 'string') return null

  const last = data.last
  const waiting = last !== null && (last.state === 'pending' || last.state === 'sent')
  const current = data.current
  const onTarget = data.onTarget === true

  const move = async () => {
    if (!window.confirm(`Move this agent to ${data.target}?\n\nThe machine will talk to this API server from now on.`)) return
    setBusy(true)
    try {
      const res = await moveDevice(id)
      if (!res.ok) {
        toast(res.error, 'Agent API server', 'error')
        return
      }
      toast(`Move to ${hostOf(res.target)} sent to the agent.`, 'Agent API server')
      await query.invalidateQueries({ queryKey: deviceApiServerQuery(id).queryKey })
    } catch (error) {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Agent API server', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="side-card" id="deviceApiServer">
      <h4>
        <i className="fas fa-exchange-alt"></i> Agent API server
      </h4>
      {last !== null ? (
        <p style={{ fontSize: 12, margin: '0 0 10px' }}>
          Last move to {hostOf(last.url)}:{' '}
          <span className={MOVE_STATE_CLASS[last.state]}>{MOVE_STATE_LABEL[last.state]}</span>{' '}
          <span className="agent-facts-sub">{last.created.slice(0, 16)}</span>
          {last.message !== '' && last.state !== 'ok' ? (
            <span className="agent-facts-sub" style={{ display: 'block' }}>
              {last.message}
            </span>
          ) : null}
        </p>
      ) : null}
      {onTarget ? (
        <p id="deviceApiServerHere" style={{ fontSize: 12, margin: 0 }}>
          <i className="fas fa-check-circle text-success"></i> Already on {hostOf(data.target)}
          {current && current.seenAt !== '' ? (
            <span className="agent-facts-sub" style={{ display: 'block' }}>
              last seen {current.seenAt.slice(0, 16)}
            </span>
          ) : null}
        </p>
      ) : (
        <p id="deviceApiServerNow" className="agent-facts-sub" style={{ margin: '0 0 8px' }}>
          {current && current.host !== '' ? (
            <>
              Currently on {current.host}
              {current.seenAt !== '' ? <> &middot; last seen {current.seenAt.slice(0, 16)}</> : null}
            </>
          ) : (
            'Current API unknown'
          )}
        </p>
      )}
      {onTarget ? null : data.kind === null ? (
        <p className="agent-facts-sub" style={{ margin: 0 }}>
          This machine does not run the Go agent, so it cannot move.
        </p>
      ) : (
        <button
          type="button"
          className="btn btn-default btn-block"
          disabled={busy || waiting || data.target === ''}
          onClick={() => void move()}
        >
          Move to {hostOf(data.target)}
        </button>
      )}
    </div>
  )
}
